import { Extension } from '@tiptap/core'
import { PluginKey } from '@tiptap/pm/state'
import StarterKit from '@tiptap/starter-kit'
import { Placeholder } from '@tiptap/extensions'
import { TaskList, TaskItem } from '@tiptap/extension-list'
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table'
import { TextAlign } from '@tiptap/extension-text-align'
import { TextStyle, Color } from '@tiptap/extension-text-style'
import Highlight from '@tiptap/extension-highlight'
import Image from '@tiptap/extension-image'
import Youtube from '@tiptap/extension-youtube'
import Suggestion from '@tiptap/suggestion'
import { filterBlockCommands } from './blockCommands'
import { fetchMentionItems, mentionPagesPending } from './api'
import { Video, Audio, FileAttachment, Bookmark, PageAndPeopleMention, FileDrop } from './mediaNodes'
import { AiReview } from './aiReview'
import { PageLinkPaste } from './pageLinkPaste'

// Suggestion menus (the "/" commands and @mentions) are rendered by React;
// the extensions only report their state and forward key presses.
const suggestionRenderer = (kind, { onSuggestionChange, onSuggestionKeyDown }) => () => ({
  onStart: props => onSuggestionChange(kind, props),
  onUpdate: props => onSuggestionChange(kind, props),
  onExit: () => onSuggestionChange(kind, null),
  onKeyDown: ({ event }) => onSuggestionKeyDown(event)
})

// People show as soon as they're known, and the page matches, if still
// pending, are added to the open menu when they arrive
const mentionSuggestion = callbacks => ({
  char: '@',
  items: ({ query, signal }) => fetchMentionItems(
    query, signal, items => callbacks.onSuggestionItems('mention', query, items)),
  command: insertMention,
  render: () => {
    const renderer = suggestionRenderer('mention', callbacks)()
    const withPending = props => ({ ...props, pagesPending: !props.loading && mentionPagesPending(props.query) })
    return {
      ...renderer,
      onStart: props => renderer.onStart(withPending(props)),
      onUpdate: props => renderer.onUpdate(withPending(props))
    }
  }
})

const SlashCommand = Extension.create({
  name: 'slashCommand',

  addOptions() {
    return {
      render: () => ({}),
      onOpenMedia: () => {},
      onOpenAi: () => {}
    }
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        pluginKey: new PluginKey('slashCommand'),
        char: '/',
        allow: ({ state, range }) => !state.doc.resolve(range.from).parent.type.spec.code,
        items: ({ query }) => filterBlockCommands(query),
        command: ({ editor, range, props }) => props.run(
          editor.chain().focus().deleteRange(range),
          { openMedia: this.options.onOpenMedia, openAi: this.options.onOpenAi }
        ),
        render: this.options.render
      })
    ]
  }
})

// Ctrl/Cmd+J opens the inline AI prompt, as in Notion
const AiShortcut = Extension.create({
  name: 'aiShortcut',

  addOptions() {
    return { onOpenAi: () => {} }
  },

  addKeyboardShortcuts() {
    return {
      'Mod-j': () => {
        this.options.onOpenAi()
        return true
      }
    }
  }
})

const insertMention = ({ editor, range, props }) => {
  // Swallow an existing space after the cursor so we don't end up with two
  const nodeAfter = editor.view.state.selection.$to.nodeAfter
  if (nodeAfter && nodeAfter.text && nodeAfter.text.startsWith(' ')) {
    range.to += 1
  }
  editor.chain().focus().insertContentAt(range, [
    {
      type: 'mention',
      attrs: { id: props.mentionId, label: props.title, kind: props.kind, mentionSuggestionChar: '@' }
    },
    { type: 'text', text: ' ' }
  ]).run()
}

const getPlaceholder = ({ node }) => {
  if (node.type.name === 'heading') {
    return `Heading ${node.attrs.level}`
  }
  return 'Type \'/\' for commands, \'@\' to mention, Ctrl+J for AI…'
}

// Comments only need a mention-capable text box, not the full document
// editor: one paragraph type, no headings/lists/tables/media/AI.
const MENTION_ONLY_OPTIONS = {
  blockquote: false,
  bold: false,
  bulletList: false,
  code: false,
  codeBlock: false,
  dropcursor: false,
  gapcursor: false,
  hardBreak: false,
  heading: false,
  horizontalRule: false,
  italic: false,
  listItem: false,
  listKeymap: false,
  link: false,
  orderedList: false,
  strike: false,
  underline: false,
  trailingNode: false
}

export const buildCommentExtensions = (callbacks, placeholder) => [
  StarterKit.configure(MENTION_ONLY_OPTIONS),
  Placeholder.configure({ placeholder: placeholder || 'Type \'@\' to mention someone…' }),
  PageAndPeopleMention.configure({ suggestion: mentionSuggestion(callbacks) })
]

export const buildExtensions = (callbacks) => [
  StarterKit.configure({
    heading: { levels: [1, 2, 3] },
    link: {
      openOnClick: false,
      autolink: true,
      defaultProtocol: 'https'
    }
  }),
  Placeholder.configure({ placeholder: getPlaceholder }),
  TextAlign.configure({ types: ['heading', 'paragraph'] }),
  TextStyle,
  Color,
  Highlight.configure({ multicolor: true }),
  TaskList,
  TaskItem.configure({ nested: true }),
  Table.configure({ resizable: false }),
  TableRow,
  TableHeader,
  TableCell,
  Image.configure({ HTMLAttributes: { class: 'rich-image' } }),
  Youtube.configure({ nocookie: true, HTMLAttributes: { class: 'rich-youtube' } }),
  Video,
  Audio,
  FileAttachment,
  Bookmark,
  PageAndPeopleMention.configure({ suggestion: mentionSuggestion(callbacks) }),
  FileDrop.configure({ onFiles: callbacks.onFiles }),
  PageLinkPaste,
  SlashCommand.configure({
    render: suggestionRenderer('slash', callbacks),
    onOpenMedia: callbacks.onOpenMedia,
    onOpenAi: callbacks.onOpenAi
  }),
  AiShortcut.configure({ onOpenAi: callbacks.onOpenAi }),
  AiReview
]
